export type ProjectContextNode = {
  kind: "project";
  id: string;
  /**
   * `plan` is the Project's own plan-and-tasks area. It replaced the retired
   * Çalışma Planı screen, which used to be a separate destination.
   */
  view: "workspace" | "map" | "workdesk" | "plan";
};

export type TaskContextNode = {
  kind: "task";
  id: string;
  view: "detail";
};

export type PlanContextNode = {
  kind: "plan";
  id: string;
  view: "detail";
};

export type RoutineContextNode = {
  kind: "routine";
  id: string;
  view: "detail";
};

export type NoteContextNode = {
  kind: "note";
  id: string;
  view: "editor" | "history" | "links" | "map";
};

export type SpanContextNode = {
  kind: "span";
  id: string;
  view: "detail";
};

export type LifeItemContextNode = {
  kind: "life_item";
  id: string;
  view: "detail";
};

export type AnalysisContextNode = {
  kind: "analysis";
  id: "read-model";
  view: "overview" | "trends" | "deviations" | "history";
};

export type KnowledgeItemContextNode = {
  kind: "knowledge_item";
  id: string;
  view: "detail";
};

export type KnowledgeMapContextNode = {
  kind: "knowledge_map";
  id: string;
  view: "canvas";
};

export type ContextWorkspaceId =
  | "planner"
  | "projects"
  | "plans"
  | "tasks"
  | "routines"
  | "notes"
  | "knowledge"
  | "orientation"
  | "analysis";

export type WorkspaceContextNode = {
  kind: "workspace";
  id: ContextWorkspaceId;
  view: "index";
};

export type EntityContextNode =
  | ProjectContextNode
  | PlanContextNode
  | TaskContextNode
  | RoutineContextNode
  | NoteContextNode
  | SpanContextNode
  | LifeItemContextNode
  | AnalysisContextNode
  | KnowledgeItemContextNode
  | KnowledgeMapContextNode;

export type ContextNode = EntityContextNode | WorkspaceContextNode;

export type ContextRootWorkspaceId = "planner" | "projects" | "knowledge" | "life" | "analysis";

const CONTEXT_VERSION = "1";
const MAX_CONTEXT_LENGTH = 1024;
const MAX_TRAIL_DEPTH = 6;
const ENTITY_ID = /^[A-Za-z0-9][A-Za-z0-9_-]{0,127}$/;
const WORKSPACE_IDS = new Set<ContextWorkspaceId>([
  "projects",
  "plans",
  "tasks",
  "routines",
  "notes",
  "planner",
  "knowledge",
  "orientation",
  "analysis",
]);

const VIEWS = {
  project: new Set(["workspace", "map", "workdesk", "plan"]),
  plan: new Set(["detail"]),
  task: new Set(["detail"]),
  routine: new Set(["detail"]),
  note: new Set(["editor", "history", "links", "map"]),
  span: new Set(["detail"]),
  life_item: new Set(["detail"]),
  analysis: new Set(["overview", "trends", "deviations", "history"]),
  knowledge_item: new Set(["detail"]),
  knowledge_map: new Set(["canvas"]),
  workspace: new Set(["index"]),
} as const;

function isValidNode(node: ContextNode): boolean {
  if (!(node.kind in VIEWS) || !VIEWS[node.kind].has(node.view as never)) return false;
  if (node.kind === "workspace") return WORKSPACE_IDS.has(node.id);
  return ENTITY_ID.test(node.id);
}

function parseNode(token: string): ContextNode | null {
  const parts = token.split(".");
  if (parts.length !== 3) return null;
  const [kind, id, view] = parts;

  const candidate = { kind, id, view } as ContextNode;
  return isValidNode(candidate) ? candidate : null;
}

/**
 * Reads a context trail made only from known entity kinds, IDs and views.
 *
 * No route or URL is ever accepted here. A malformed token invalidates the
 * whole trail, so a crafted query cannot turn the back affordance into an open
 * redirect or smuggle an unrelated destination into the application shell.
 */
export function parseContextTrail(value: unknown): ContextNode[] {
  if (typeof value !== "string" || value.length === 0 || value.length > MAX_CONTEXT_LENGTH) {
    return [];
  }

  const [version, ...tokens] = value.split("~");
  if (version !== CONTEXT_VERSION || tokens.length === 0 || tokens.length > MAX_TRAIL_DEPTH) {
    return [];
  }

  const nodes = tokens.map(parseNode);
  return nodes.every((node): node is ContextNode => node !== null) ? nodes : [];
}

export function serializeContextTrail(nodes: readonly ContextNode[]): string | undefined {
  if (nodes.length === 0 || nodes.length > MAX_TRAIL_DEPTH || !nodes.every(isValidNode)) {
    return undefined;
  }
  return [CONTEXT_VERSION, ...nodes.map((node) => `${node.kind}.${node.id}.${node.view}`)].join(
    "~",
  );
}

function sameNode(left: ContextNode, right: ContextNode): boolean {
  return left.kind === right.kind && left.id === right.id && left.view === right.view;
}

/** Adds the page being left while keeping the chain finite and loop-free. */
export function appendContextNode(
  trail: readonly ContextNode[],
  current: ContextNode,
): ContextNode[] {
  if (!isValidNode(current)) return [];
  const safeTrail = trail.length <= MAX_TRAIL_DEPTH && trail.every(isValidNode) ? [...trail] : [];
  const repeatedAt = safeTrail.findIndex((node) => sameNode(node, current));
  if (repeatedAt >= 0) return safeTrail.slice(0, repeatedAt + 1);

  if (safeTrail.length < MAX_TRAIL_DEPTH) return [...safeTrail, current];

  // The first entry determines workspace ownership, so retain it while
  // discarding only the oldest intermediate hop.
  return [safeTrail[0]!, ...safeTrail.slice(-(MAX_TRAIL_DEPTH - 2)), current];
}

export function contextParamFromSearch(search: unknown): string | undefined {
  if (search instanceof URLSearchParams) return search.get("ctx") ?? undefined;
  if (typeof search === "string") return new URLSearchParams(search).get("ctx") ?? undefined;
  if (!search || typeof search !== "object") return undefined;
  const value = (search as Record<string, unknown>).ctx;
  return typeof value === "string" ? value : undefined;
}

export function getContextRootWorkspaceId(value: unknown): ContextRootWorkspaceId | undefined {
  const first = parseContextTrail(value)[0];
  if (!first) return undefined;
  if (first.kind === "project") return "projects";
  if (first.kind === "note" || first.kind === "knowledge_item" || first.kind === "knowledge_map") {
    return "knowledge";
  }
  if (first.kind === "span") return "planner";
  if (first.kind === "life_item") return "life";
  if (first.kind === "analysis") return "analysis";
  if (first.kind === "plan" || first.kind === "task" || first.kind === "routine") {
    return "planner";
  }
  if (first.id === "projects") return "projects";
  if (first.id === "notes" || first.id === "knowledge") return "knowledge";
  // Keep accepting the retired token in saved context trails, but converge its
  // destination on the generic Yaşam Rehberi workspace.
  if (first.id === "orientation") return "life";
  if (first.id === "analysis") return "analysis";
  return "planner";
}

export function getContextRootWorkspaceIdFromSearch(
  search: unknown,
): ContextRootWorkspaceId | undefined {
  return getContextRootWorkspaceId(contextParamFromSearch(search));
}

export function popContextTrail(
  value: unknown,
  fallback: ContextNode,
): { destination: ContextNode; remaining: ContextNode[] } {
  const trail = parseContextTrail(value);
  const destination = trail.at(-1);
  return destination
    ? { destination, remaining: trail.slice(0, -1) }
    : { destination: fallback, remaining: [] };
}
